<template>
    <div class="space-y-2">
        <template v-if="propertyDefinition.type === 'string'">
            <form-select v-if="contextVariables.length" name="Source"
                :model-value="stringSource" @update:model-value="setStringSource">
                <option value="literal">Fixed value</option>
                <option value="event">Event value</option>
            </form-select>
            <form-select v-if="stringSource === 'event'" name="Event value"
                :model-value="stringVariable" @update:model-value="setStringVariable">
                <option v-for="variable in contextVariables" :key="variable.id" :value="variable.token">
                    {{ variable.label }} → {{ variable.id }}
                </option>
            </form-select>
            <div v-else class="flex items-center gap-2">
                <form-label class="w-2/5 shrink-0">Value</form-label>
                <button class="flex-1 truncate rounded border border-gray-400 bg-white px-2 py-1 text-left text-sm text-gray-900 hover:border-gray-500 focus:border-blue-500 focus:outline-none"
                    @click="showBuilder = true">
                    <span v-if="modelValue !== ''">{{ modelValue }}</span>
                    <span v-else class="text-gray-500">Build value…</span>
                </button>
            </div>
        </template>
        <template v-else-if="['int', 'float', 'number'].includes(propertyDefinition.type)">
            <form-input name="Value" type="number"
                :min="propertyDefinition.metadata.min" :max="propertyDefinition.metadata.max"
                :step="propertyDefinition.metadata.step" :model-value="numericVariable ? '' : modelValue"
                :disabled="Boolean(numericVariable)"
                @update:model-value="$emit('update:modelValue', $event)" />
            <form-select v-if="contextVariables.length"
                name="Event value" :model-value="numericVariable"
                @update:model-value="setNumericVariable">
                <option value="">Use literal value</option>
                <!-- Named by the token the action stores (`val`), not by the friendly
                     label, so the option matches what the event actually provides. -->
                <option v-for="variable in contextVariables" :key="variable.id" :value="variable.token">
                    {{ variable.id }}
                </option>
            </form-select>
        </template>
        <div v-else-if="propertyDefinition.type === 'color'" class="flex items-center gap-2">
            <form-label class="w-2/5 shrink-0">Value</form-label>
            <ColorPicker :pureColor="modelValue"
                @update:pureColor="$emit('update:modelValue', $event)"
                @pureColorChange="$emit('update:modelValue', $event)"
                useType="pure" :format="propertyDefinition.metadata.format ?? 'hex'"
                lang="En" shape="circle" />
        </div>
        <form-select v-else-if="propertyDefinition.type === 'enum'" name="Value"
            :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)">
            <option v-for="option in propertyDefinition.metadata.values ?? []"
                :key="optionValue(option)" :value="optionValue(option)">{{ optionLabel(option) }}</option>
        </form-select>
        <form-select v-else-if="propertyDefinition.type === 'bool'" name="Value"
            :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)">
            <option :value="true">True</option><option :value="false">False</option>
        </form-select>
        <div v-else-if="propertyDefinition.type === 'resource'" class="flex items-center gap-2">
            <form-label class="w-2/5 shrink-0">Value</form-label>
            <component v-if="resourceEditor" :is="resourceEditor"
                :model-value="modelValue"
                @update:model-value="$emit('update:modelValue', $event)" />
            <ResourcePicker v-else :resource-type="propertyDefinition.metadata.resourceType"
                :model-value="modelValue"
                @update:model-value="$emit('update:modelValue', $event)" />
        </div>
        <form-input v-else name="Value" :model-value="modelValue"
            @update:model-value="$emit('update:modelValue', $event)" />
        <IconPicker v-model="showBuilder" :initial-text="String(modelValue ?? '')"
            :available-tabs="['icons', 'variables']" :extra-variables="contextVariables"
            :preview-values="previewValues" @text-updated="$emit('update:modelValue', $event)" />
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import IconPicker from '../../IconPicker.vue';
import { ColorPicker } from 'vue3-colorpicker';
import 'vue3-colorpicker/style.css';
import ResourcePicker from '../../resources/ResourcePicker.vue';
import { getPropertyEditorComponent } from '../../resources/resourceUIRegistry.js';
const props = defineProps({
    modelValue: { required: false }, propertyDefinition: { type: Object, required: true },
    contextVariables: { type: Array, default: () => [] },
});
const emit = defineEmits(['update:modelValue']);
const showBuilder = ref(false);
const previewValues = computed(() => Object.fromEntries(props.contextVariables.map(variable =>
    [variable.id, variable.preview])));
const stringVariable = computed(() =>
    props.contextVariables.some(variable => variable.token === props.modelValue)
        ? props.modelValue : '');
const stringSource = computed(() => stringVariable.value ? 'event' : 'literal');
const numericVariable = computed(() =>
    props.contextVariables.some(variable => variable.token === props.modelValue) ? props.modelValue : '');
const resourceEditor = computed(() =>
    getPropertyEditorComponent(props.propertyDefinition.metadata.resourceType));
function setNumericVariable(value) {
    emit('update:modelValue', value || (props.propertyDefinition.default ??
        props.propertyDefinition.metadata.min ?? 0));
}
function setStringSource(value) {
    if (value === 'event') {
        emit('update:modelValue', stringVariable.value || props.contextVariables[0]?.token || '');
    } else if (stringVariable.value) {
        emit('update:modelValue', '');
    }
}
function setStringVariable(value) {
    emit('update:modelValue', value);
}
const optionValue = option => option && typeof option === 'object' ? option.value : option;
const optionLabel = option => option && typeof option === 'object' ? (option.label ?? option.value) : option;
</script>
